import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Card,
  CardActions,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FunctionComponent } from 'react';

import {
  AdminEventTransactionsQuery,
  TransactionStatus,
  TransactionType,
} from '../../apollo/__generated__/graphql';
import { Dialog } from '../../components/Dialog';
import { Tooltip } from '../../components/Tooltip';
import { Layout } from '../../navigation/components/Layout/Layout';
import { useAdminEventTransactions } from './useAdminEventTransactions';

const statusSelectOptions: TransactionStatus[] = [
  TransactionStatus.Paid,
  TransactionStatus.Pending,
  TransactionStatus.Cancelled,
  TransactionStatus.Refunded,
  TransactionStatus.Error,
];

const transactionTypeOptions: TransactionType[] = [
  TransactionType.Ticket,
  TransactionType.Bedroom,
];

const formatVoucherAmount = (amount: number) => `-${amount / 100}€`;
const formatFullName = (firstname?: string | null, lastname?: string | null) =>
  `${firstname ?? ''} ${lastname ?? ''}`.trim();
const copyToClipboard = async (value: string) => {
  await navigator.clipboard.writeText(value);
};

export const AdminEventTransactions: FunctionComponent = () => {
  const {
    transactions,
    loading,
    dialog,
    onCloseDialog,
    onRefundPress,
    onDownloadCsv,
    onTransactionStatusChange,
    onTransactionTypeChange,
    transactionStatus,
    transactionTypes,
    clearSearch,
    onSearchChange,
    search,
  } = useAdminEventTransactions();

  const columns: GridColDef<
    AdminEventTransactionsQuery['eventTransactions'][number]
  >[] = [
    {
      field: 'user',
      headerName: 'Participant',
      renderCell: (params) =>
        formatFullName(params.row.user.firstname, params.row.user.lastname),
    },
    {
      field: 'payer',
      headerName: 'Payeur',
      renderCell: (params) =>
        formatFullName(params.row.payer.firstname, params.row.payer.lastname),
    },
    {
      field: 'user.email',
      headerName: 'Email participant',
      width: 220,
      renderCell: (params) => params.row.user.email,
    },
    {
      field: 'payer.email',
      headerName: 'Email payeur',
      width: 260,
      renderCell: (params) => {
        const payerEmail = params.row.payer.email;

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={0.5}
            width="100%"
            height="100%"
            minWidth={0}
          >
            <Typography noWrap variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              {payerEmail}
            </Typography>
            {payerEmail ? (
              <Tooltip title="Copier l'email">
                <IconButton
                  size="small"
                  sx={{ flexShrink: 0, alignSelf: 'center' }}
                  onClick={() => copyToClipboard(payerEmail)}
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Statut',
    },
    {
      field: 'type',
      headerName: 'Type',
    },
    {
      field: 'voucher',
      headerName: 'Réduction',
      width: 120,
      renderCell: (params) => {
        if (!params.row.voucher) {
          return null;
        }

        return formatVoucherAmount(params.row.voucher.amount);
      },
    },
    {
      field: 'helloAssoOrderId',
      headerName: 'HelloAsso OID',
    },
    {
      field: 'helloAssoCheckoutIntentId',
      headerName: 'HelloAsso CID',
    },
    {
      field: 'createdAt',
      headerName: 'Créé le',
    },
    {
      field: 'updatedAt',
      headerName: 'Màj le',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      renderCell: (params) => {
        if (params.row.status !== TransactionStatus.Paid) {
          return null;
        }

        return (
          <Tooltip title="Rembourser la transaction">
            <IconButton
              color="warning"
              onClick={() => onRefundPress(params.row)}
            >
              <UndoIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Layout
      title="Administration - Événement - Transactions"
      activeTab="adminEventTransactions"
    >
      <Card sx={{ mb: 2 }}>
        <CardActions sx={{ justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            onClick={onDownloadCsv}
            disabled={transactions.length <= 0}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Télécharger le CSV
          </Button>
        </CardActions>
        <CardActions>
          <Select<TransactionStatus[]>
            value={transactionStatus}
            onChange={onTransactionStatusChange}
            size="small"
            multiple
            displayEmpty
            renderValue={(selected) =>
              selected.length === 0 ? 'Statut' : selected.join(', ')
            }
          >
            {statusSelectOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          <Select<TransactionType[]>
            value={transactionTypes}
            onChange={onTransactionTypeChange}
            size="small"
            multiple
            displayEmpty
            renderValue={(selected) =>
              selected.length === 0 ? 'Type' : selected.join(', ')
            }
          >
            {transactionTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label="Rechercher un participant"
            size="small"
            value={search}
            onChange={onSearchChange}
            InputProps={{
              startAdornment: <SearchIcon />,
              endAdornment:
                search.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} onMouseDown={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />
        </CardActions>
      </Card>
      <DataGrid
        rows={transactions}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        loading={loading}
      />
      {dialog ? (
        <Dialog
          title={dialog.title}
          description={dialog.description}
          onSubmit={dialog.onSubmit}
          onCancel={onCloseDialog}
          submitText="Rembourser"
          isDestructive
          open
        />
      ) : null}
    </Layout>
  );
};
